// 261005 E1-P1 — hợp đồng giữa vỏ và các chặng (Phase doc Bước 2b + "Leader chốt sau Dev review").
import { createContext, useContext, useEffect, useRef } from 'react';
import type { OutlineRecord } from '../../../types/adaptation';
import type { WorkbenchStageKey, WorkbenchStageState, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { AdaptationErrorView } from '../adaptationUtils';
import type { AssistantIntent, AssistantRequest } from './assistant/assistantIntent';
import type { AdaptationData } from './useAdaptationData';
import type { BgJobApi } from './useBgJob';
import type { SeasonReaderApi } from './useSeasonReader';
import type { OpenEpisodeTarget } from './reviewBookView';
import type { ScriptJobApi } from './useScriptJob';
import { scrollWhenReady } from './useScrollWhenReady';
import type { StageNavRow } from './stageNavView';
import { ctaOwnerOf, holdsCta } from './wbButtonView';
import type { CtaOwner } from './wbButtonView';
import { intentAnchor, WorkbenchTarget } from './workbenchView';
import type { TopMenuExtra, TopMenuSource } from './topMenuView';

export interface WorkbenchCtx {
  storyId: string;
  storyName?: string;
  /** Link nguồn nhập lúc tạo truyện — ① điền sẵn vào ô link khi còn trống. */
  storyLink?: string | null;
  data: AdaptationData;
  workbench: WorkbenchView | null;
  /** `workbench.refresh()` + `data.reload()`. Gọi sau MỌI thao tác ghi thành công. */
  refreshAll: () => Promise<OutlineRecord | null>;
  /** Tăng sau mỗi `refreshAll` — thay cho `refreshToken` cũ của SeasonReaderCard. */
  dataVersion: number;
  /** MỘT hộp lỗi có mã (và nút phục hồi) ở vỏ. */
  showError: (v: AdaptationErrorView) => void;
  intent: WorkbenchTarget | null;
  clearIntent: () => void;
  /** Đổi chặng (giữ `mode`/`tab` ở URL) và đặt `intent` nếu có. */
  goTo: (t: WorkbenchTarget) => void;
  scriptJob: ScriptJobApi;
  bgJob: BgJobApi;
  /** Job của phiên này HOẶC `workbench.jobs.running` — khoá nút ghi. */
  busy: boolean;
  stageState: (s: WorkbenchStageKey) => WorkbenchStageState | undefined;
  /** 261005 E1-P2 — độ rộng cột danh sách tập của ⑤ (`widths.list` của vỏ). */
  listWidth: number;
  setListWidth: (px: number) => void;
  resetListWidth: () => void;
  /**
   * 261005 E2-P2a — tập đang mở ở ⑤ mà khung Trợ lý có thể nói về (CÓ kịch bản + workbench đã tải). Không ở ⑤ / chưa có ⇒ null.
   * `scriptEpisodeNoScript` = tập đang chọn nhưng CHƯA có kịch bản (chỉ để khung nêu lý do, R3).
   */
  scriptEpisode: number | null;
  scriptEpisodeNoScript: number | null;
  setScriptEpisode: (ep: number | null, noScriptEp?: number | null) => void;
  /**
   * 261005 E2-P2b — ⑤ nhờ khung Trợ lý nói về một tập (tick lỗi / điền câu gợi ý). KHÔNG tự gửi. Vỏ sinh `nonce` + mở lại khung nếu đang Thu gọn.
   * Khung xử lý đúng MỘT lần rồi `clearAssistantIntent(nonce)`; không truyền nonce ⇒ xoá intent đang chờ (người dùng tự chọn tập khác).
   */
  assistantIntent: AssistantIntent | null;
  requestAssistant: (r: AssistantRequest) => void;
  clearAssistantIntent: (nonce?: number) => void;
  /**
   * 261005 E4-P1 — sửa tay kịch bản: tập đang ở chế độ sửa (null = không). Khung Trợ lý tắt mọi nút GHI của tập đó kèm lý do.
   * `scriptSaveNonce` tăng sau mỗi lần LƯU sửa tay thành công ⇒ khung Trợ lý tải lại scope + luồng + phiên bản (như sau khi Áp khối).
   */
  scriptEditing: number | null;
  setScriptEditing: (ep: number | null) => void;
  scriptSaveNonce: number;
  notifyScriptSaved: () => void;
  /** 261005 E4-P4b — id đợt "Xử lý phần mới" đã đóng thông báo (dải + Alert ở ⑤) và hàm đóng. */
  appendDismissedId: string | null;
  dismissAppend: (id: string) => void;
  /** 261005 E5 — khe do vỏ cấp để chặng "bơm" nội dung vào thanh trái / thanh trên (React portal; props + handler giữ nguyên ở chặng). */
  slots: WorkbenchSlots;
  /** Khung Trợ lý đang mở (⑤ ẩn nút "Trợ lý AI" trên thanh trên khi đã mở). */
  assistantOpen: boolean;
  /** 261005 E5 — bề ngang vùng giữa (px) để chặng gom nút vào menu "⋯" khi hẹp. */
  mainWidth: number;
  /** Mục của BƯỚC gửi lên menu "⋯" duy nhất của thanh trên (nguồn `stage`; xem `topMenuView`). */
  setTopMenuExtra: (items: TopMenuExtra[]) => void;
  /** 3k-c (SA NOTE 1): mỗi nguồn (`stage` · `list` · `episode`) ghi RIÊNG, vỏ GỘP — danh sách tập và tập đang mở cùng hiện, không đè nhau. */
  setTopMenuSource: (source: TopMenuSource, items: TopMenuExtra[]) => void;
  /** Mục ĐÃ GỘP theo thứ tự bước → danh sách → tập (mỗi mục mang `group`). */
  topMenuExtra: TopMenuExtra[];
  /**
   * 261023 E3-P3a — «AI đọc cả mùa» dùng chung của ⑤ và Sổ duyệt (vỏ giữ MỘT hook: đổi qua lại ⑤ ↔ Sổ không tải lại; chặng ①–④ không gọi thêm).
   * Chỉ tải khi đang ở ⑤ hoặc Sổ và truyện đã có kịch bản.
   */
  seasonReader: SeasonReaderApi;
  /** 261023 E3-P3a — Sổ duyệt (`?view=review`) đang mở? Mở Sổ / mở một tập từ Sổ (đẩy lịch sử để nút Back về lại Sổ). */
  reviewing: boolean;
  openReview: () => void;
  /** 3h1 — số việc «cần bạn» của Sổ duyệt (không gồm tên gốc rò) — dải ghi lên nút «Mở sổ duyệt». */
  reviewNeed: number;
  openEpisodeFromReview: (t: OpenEpisodeTarget) => void;
  /** 3h2 — 5 hàng cột bước đã tính ở vỏ (`navRowsFor`): hộp V12 / dòng «chưa chạy» của chặng đọc chung từ đây. null khi chưa tải workbench. */
  navRows: StageNavRow[] | null;
  /** 3h2 — truyện đang chạy chuỗi «Truyện mới» (hoặc đang ở nút Bắt đầu): dải giữ việc, bỏ «Tiếp theo: …». */
  chainActive: boolean;
  /** 3k-a — mở «? Hướng dẫn» (đã chuyển vào «⋯»). */
  startTour: () => void;
  /** 3h3 (H3/H10) — ĐÚNG MỘT nút đỏ mỗi màn: dải (hoặc dải «Phần mới») có nút chính bấm được; số tập đã tick ở Sổ. `useCtaHolds` quyết ai được đỏ. */
  bandCta: boolean;
  setBandCta: (key: string, v: boolean) => void;
  reviewTick: number;
  setReviewTick: (n: number) => void;
}

export type { TopMenuExtra, TopMenuSource };

export interface WorkbenchSlots {
  /** 3k-c — thanh trái dưới 5 bước: danh sách phụ của chặng (⑤ = danh sách tập, có ô tick + nút viết). */
  side: HTMLElement | null;
  /** 3k-c — đáy thanh trái (⑤ = «Đẩy N tập» khi truyện không chạy chuỗi, rồi «Đọc cả mùa»). */
  sideFoot: HTMLElement | null;
  /** Thanh trên, nhóm nút chữ (⑤ = «Sửa» của tập đang mở; 3k-a: Phiên bản / Viết lại / Đẩy riêng đã vào «⋯»). */
  topSecondary: HTMLElement | null;
  /** Thanh trên, nút chính + menu ⋯ của chặng. */
  topPrimary: HTMLElement | null;
}

const Ctx = createContext<WorkbenchCtx | null>(null);
export const WorkbenchProvider = Ctx.Provider;

/** Ai được đỏ ngay lúc này (hàm thuần `ctaOwnerOf`). `holds('stage')` cũng là điều kiện của thanh dính ⑤. */
export const useCtaHolds = (): { owner: CtaOwner; holds: (who: 'sticky' | 'pushbar' | 'band' | 'stage') => boolean } => {
  const v = useContext(Ctx);
  const owner = ctaOwnerOf({ bandHoldsCta: !!v?.bandCta, reviewTicked: !!v?.reviewing && (v?.reviewTick ?? 0) > 0 });
  return { owner, holds: (who) => holdsCta(owner, who) };
};

export const useWorkbenchCtx = (): WorkbenchCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useWorkbenchCtx phải nằm trong <WorkbenchProvider>');
  return v;
};

/**
 * Chặng gọi: xử lý `intent` hướng tới mình ĐÚNG MỘT LẦN rồi `clearIntent()`.
 * `handler` làm phần "mở modal / đổi trang…"; sau đó cuộn tới phần tử đích (nếu có) khi nó đã render.
 */
export const useStageIntent = (stage: WorkbenchStageKey, handler?: (t: WorkbenchTarget) => void): void => {
  const { intent, clearIntent } = useWorkbenchCtx();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!intent || intent.stage !== stage) return undefined;
    handlerRef.current?.(intent);
    const id = intentAnchor(intent);
    if (!id) { clearIntent(); return undefined; }
    return scrollWhenReady(id, () => clearIntent());
    // chỉ chạy khi intent đổi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);
};
